/**
 * O que decide se o clube criado pode nascer: a sigla, que é única dentro do
 * mundo da carreira, e o filtro de termos ofensivos do nome e da sigla.
 *
 * Nada aqui desenha e nada aqui traduz. O que sai é chave de i18n e dado, como
 * em toda fronteira desta pasta, porque este arquivo não sabe em que idioma a
 * pessoa joga e escrever a frase aqui a devolveria em português para quem
 * escolheu inglês.
 *
 * O filtro tem **duas saídas, e não uma**, e essa é a decisão que molda o
 * arquivo inteiro. Barrar tudo que parece palavrão recusaria Caralhote, que é
 * peixe, e Disputa, que carrega um palavrão inteiro no meio da palavra, sem dar
 * recurso a quem só quis nomear o clube da cidade. Marcar tudo encheria a fila
 * de revisão de clube honesto. Então o que bate exato numa palavra da lista
 * barra na hora, e o que bate por pedaço ou por aproximação deixa criar e sai
 * marcado para quem revisa.
 *
 * As listas ficam aqui dentro, escritas à mão, e não num arquivo de dados: cada
 * termo é uma decisão que alguém tomou olhando para ele, e várias linhas só se
 * entendem com o comentário do lado.
 */

// ---------------------------------------------------------------------------
// Normalização
// ---------------------------------------------------------------------------

/**
 * As trocas de teclado que escondem a palavra.
 *
 * São as cinco que a pessoa usa de verdade, mais o arroba e o cifrão, que são a
 * mesma ideia com outro símbolo. O ponto de exclamação ficou de fora de
 * propósito: ele vale por i no meio da palavra, mas no fim ele é pontuação, e
 * trocá-lo faria "Puta!" virar outra palavra e escapar.
 */
const TROCAS_DE_TECLADO: Record<string, string> = {
  "4": "a",
  "@": "a",
  "3": "e",
  "1": "i",
  "0": "o",
  "5": "s",
  $: "s",
};

/** Sem acento e sem cedilha, que é como as listas estão escritas. */
function semAcento(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * O texto reduzido ao que importa para comparar: minúsculas, sem acento, com a
 * troca de teclado desfeita e com tudo que não é letra virando separador.
 *
 * Pontuação vira espaço em vez de sumir porque as duas leituras interessam. As
 * palavras separadas pegam "Puta FC", e a colagem de todas elas pega "P.U.T.A",
 * que é a evasão mais comum.
 */
function normalizar(texto: string): string {
  let saida = "";
  for (const letra of semAcento(texto).toLowerCase()) {
    const trocada = TROCAS_DE_TECLADO[letra] ?? letra;
    saida += /[a-z]/.test(trocada) ? trocada : " ";
  }
  return saida.trim().replace(/\s+/g, " ");
}

/**
 * A palavra e o singular dela, quando o plural é só um s no fim.
 *
 * Sem isso "Putas" passa por onde "Puta" é barrado, e repetir cada termo no
 * plural dobraria a lista. O corte em quatro letras protege as palavras curtas
 * que terminam em s por natureza, como dos e nos.
 */
function formasDaPalavra(palavra: string): string[] {
  if (palavra.length > 3 && palavra.endsWith("s")) return [palavra, palavra.slice(0, -1)];
  return [palavra];
}

/**
 * A palavra com as letras parecidas trazidas de volta.
 *
 * Quem quer escapar do filtro troca letra por outra que se lê igual: `pvta`,
 * `fuk`, `cuz`, `shyt`. Conferido antes de escrever isto, os três primeiros
 * saíam livres, sem nem marca.
 *
 * **Isto só alimenta a marca, nunca a recusa.** A troca destrói palavra de
 * verdade: Kaká vira caca e Cruzeiro vira cruseiro. Recusar por aqui seria
 * barrar nome de gente por causa de uma regra de aproximação, que é exatamente
 * o erro que a lista de nomes legítimos existe para consertar.
 */
const LETRAS_PARECIDAS: Record<string, string> = {
  v: "u",
  k: "c",
  y: "i",
  z: "s",
  w: "v",
  q: "c",
};

function comLetrasParecidas(palavra: string): string {
  let saida = "";
  for (const letra of palavra) saida += LETRAS_PARECIDAS[letra] ?? letra;
  return saida;
}

/** Letra repetida colapsada, que é como se escreve "puuuta" para escapar. */
function semRepetidas(palavra: string): string {
  return palavra.replace(/(.)\1+/g, "$1");
}

// ---------------------------------------------------------------------------
// As listas
// ---------------------------------------------------------------------------

/**
 * O que barra na hora, sempre escrito já normalizado.
 *
 * Só entra aqui o que não tem segunda leitura. Termo de dois sentidos desce
 * para a lista de baixo, mesmo quando é ofensivo na maioria das vezes, porque o
 * preço de errar aqui é uma pessoa sem recurso e o preço de errar lá é alguém
 * lendo uma linha a mais na revisão.
 *
 * Quem não está aqui, e a razão: macaco e veado são bichos e estão no catálogo
 * de figuras, negro é cor e é rio de clube de verdade, e preto é cor de camisa.
 * Um filtro que barra o nome do animal do escudo é um filtro quebrado.
 */
const BLOQUEADOS: ReadonlySet<string> = new Set([
  // pt-BR
  "caralho",
  "porra",
  "buceta",
  "boceta",
  "xoxota",
  "xereca",
  "piroca",
  "punheta",
  "punheteiro",
  "boquete",
  "foder",
  "fodase",
  "fodido",
  "fodida",
  "puta",
  "putas",
  "putaria",
  "putinha",
  "filhodaputa",
  "filhadaputa",
  "cuzao",
  "arrombado",
  "arrombada",
  "escroto",
  "merda",
  "bosta",
  "cu",
  // As siglas de xingamento existem porque a sigla tem três letras e é o campo
  // mais fácil de burlar: sem elas, o que o nome barra a sigla deixa passar.
  "fdp",
  "pqp",
  "vsf",
  "vtnc",
  "traveco",
  "viadinho",
  "mongoloide",
  // Ódio, nos três idiomas. Nome de líder e de regime entram porque é o que
  // aparece, e nenhum deles tem outra leitura num nome de clube.
  "nazista",
  "nazismo",
  "nazi",
  "hitler",
  "heilhitler",
  // en
  "fuck",
  "fucker",
  "fucking",
  "motherfucker",
  "shit",
  "bullshit",
  "bitch",
  "cunt",
  "asshole",
  "dickhead",
  "whore",
  "slut",
  "nigger",
  "nigga",
  "faggot",
  "fag",
  "chink",
  "spic",
  "wetback",
  "tranny",
  // es
  "mierda",
  "cabron",
  "pendejo",
  "gilipollas",
  "hijodeputa",
  "hijueputa",
  "malparido",
  "chingar",
  "chinga",
  "chingada",
  "joto",
  "maricon",
  "sudaca",
  "negrata",
]);

/**
 * O que deixa criar e sai marcado para revisão.
 *
 * Duas famílias moram aqui. A primeira é a palavra de dois sentidos, e cada uma
 * tem um nome de clube plausível do outro lado: piranha é peixe, pinto é
 * sobrenome, cacete é o pão e o porrete, rola é a ave, corno é o chifre, verga
 * é o mastro, concha é a praia, cono é o cone em espanhol e kike é o apelido de
 * Enrique. Barrar qualquer uma delas recusaria um clube honesto.
 *
 * A segunda é o xingamento leve, que ofende sem chocar. Ele não trava a criação
 * porque o dono do clube é quem vai conviver com o nome, mas fica visível para
 * quem revisa se alguém reclamar depois.
 *
 * O caso mais brasileiro da lista é kkk: aqui é risada e no inglês é a Klan.
 * Num jogo com clube de todo canto do mundo, ele só pode marcar.
 */
const DUVIDOSOS: ReadonlySet<string> = new Set([
  // pt-BR
  "pinto",
  "pau",
  "rola",
  "cacete",
  "corno",
  "bunda",
  "peido",
  "cagada",
  "babaca",
  "otario",
  "vagabundo",
  "vagabunda",
  "desgracado",
  "idiota",
  "imbecil",
  "piranha",
  "viado",
  "bicha",
  "boiola",
  "sapatao",
  "crioulo",
  "neguinho",
  "retardado",
  "favelado",
  "kkk",
  // en
  "damn",
  "hell",
  "crap",
  "dick",
  "retard",
  "rape",
  "kike",
  // es
  "puto",
  "culo",
  "cono",
  "zorra",
  "polla",
  "verga",
  "pinche",
  "marica",
  "concha",

  /*
   * Grafias de fuga curtas demais para qualquer regra pegar.
   *
   * `fuk` e `cuz` têm três letras: nenhum mínimo de pedaço as alcança sem
   * transformar o filtro num moedor, e a troca de letra parecida não chega
   * lá porque falta letra, não sobra. Entram à mão, e entram aqui e não na
   * lista dura, porque `cuz` é gíria inglesa de primo e não vale travar
   * ninguém por causa disso.
   */
  "fuk",
  "fuq",
  "cuz",
  "xit",
]);

/**
 * Palavras que são nome de gente ou de lugar antes de serem qualquer coisa.
 *
 * Esta lista vence as duas de cima. É o remédio conhecido para o problema de
 * Scunthorpe: filtro que olha só a forma da palavra recusa time que existe há
 * cem anos, e quem escreve o nome do próprio bairro não tem como adivinhar
 * que perdeu.
 *
 * Cada linha aqui é uma recusa que aconteceu de verdade na conferência, e não
 * um medo hipotético:
 *
 * - `porras` é sobrenome espanhol e panamenho, inclusive de presidente, e caía
 *   na lista dura porque o plural vira `porra` ao tirar o s.
 * - `marica` é município do Rio de Janeiro com clube federado.
 * - `disputa` e `cuiaba` contêm termo curto dentro, e Cuiabá está na Série A.
 * - `scunthorpe` é o caso que deu nome ao problema.
 * - `pau` é clube da segunda divisão francesa.
 * - `bagre`, `pinta`, `xavante` e `peniche` passam hoje, e ficam aqui para
 *   continuarem passando no dia em que alguém apertar as regras.
 *
 * Nome composto entra pela palavra, e não pela frase: quem confere olha palavra
 * por palavra.
 */
const NOMES_LEGITIMOS: ReadonlySet<string> = new Set([
  "porras",
  "porras",
  "marica",
  "maricas",
  "disputa",
  "disputas",
  "cuiaba",
  "scunthorpe",
  "pau",
  "paus",
  "bagre",
  "pinta",
  "xavante",
  "peniche",
  "essex",
  "sussex",
  "middlesex",
  "penistone",
  "arsenal",
  "putativo",
]);

/**
 * De quantas letras um termo precisa para valer como pedaço dentro de outra
 * palavra.
 *
 * Abaixo de quatro, pedaço é ruído puro: `cu` está dentro de Cuiabá e de
 * açucar, e não sobra sinal nenhum.
 *
 * Em cinco, que era o número antes, sobrava buraco grande demais do outro
 * lado: `puta`, `fuck`, `shit` e `nazi` têm quatro letras, então ficavam de
 * fora e bastava uma letra a mais para passar limpo. Conferido: `Putax`,
 * `Xputa` e `Puta1` saíam livres, sem nem ir para revisão.
 *
 * Quatro com a lista de nomes legítimos por cima é a troca certa: Cuiabá,
 * Disputa e Scunthorpe estão protegidos por nome, e o resto que casar vai para
 * revisão, que é onde erro custa uma linha lida e não uma pessoa travada.
 */
const PEDACO_MINIMO = 4;

const PEDACOS: readonly string[] = [...BLOQUEADOS].filter(
  (termo) => termo.length >= PEDACO_MINIMO,
);

// ---------------------------------------------------------------------------
// O exame do texto
// ---------------------------------------------------------------------------

/** Os três desfechos de um texto examinado. */
export type VeredictoDoTexto = "livre" | "revisar" | "bloqueado";

export interface ExameDeTexto {
  veredicto: VeredictoDoTexto;
  /** O termo da lista que bateu, guardado para quem revisa. */
  termo: string | null;
}

/** O termo da lista que bate exato numa palavra, ou na colagem de todas elas. */
function acharExato(palavras: string[], colado: string, lista: ReadonlySet<string>): string | null {
  for (const palavra of palavras) {
    // Nome de gente ou de lugar não é examinado: a lista de legítimos existe
    // justamente para vencer a forma da palavra.
    if (NOMES_LEGITIMOS.has(palavra)) continue;
    for (const forma of formasDaPalavra(palavra)) {
      if (lista.has(forma)) return forma;
    }
  }
  if (colado.length > 0 && palavras.length > 1 && lista.has(colado)) return colado;
  return null;
}

/**
 * O que só se parece com um termo da lista.
 *
 * A aproximação por letra repetida marca e nunca barra porque colapsar letra
 * repetida destrói o português de verdade: passo vira paso e Mossoró vira
 * Mosoro. O que se acha assim é palpite, e palpite não recusa ninguém.
 *
 * **O pedaço é procurado dentro de cada palavra, e nunca na colagem delas.**
 * A colagem inventa termo na emenda de duas palavras inocentes: conferido,
 * "Disputa Sport" virava `disputasport`, que contém `putas`, e um nome
 * perfeitamente normal ia para a fila de revisão por causa de onde a segunda
 * palavra começa. Palavra por palavra, isso não acontece.
 */
function acharParecido(palavras: string[], colado: string): string | null {
  const duvidoso = acharExato(palavras, colado, DUVIDOSOS);
  if (duvidoso) return duvidoso;

  for (const palavra of [...palavras, colado]) {
    const curta = semRepetidas(palavra);
    if (curta !== palavra && (BLOQUEADOS.has(curta) || DUVIDOSOS.has(curta))) return curta;

    const parecida = comLetrasParecidas(palavra);
    if (parecida !== palavra && (BLOQUEADOS.has(parecida) || DUVIDOSOS.has(parecida))) {
      return parecida;
    }
  }

  for (const palavra of palavras) {
    if (NOMES_LEGITIMOS.has(palavra)) continue;
    for (const termo of PEDACOS) {
      // `palavra.length > termo.length` porque igualdade já é caso de
      // `acharExato`, e tratá-la aqui viraria marca no que devia ser recusa.
      if (palavra.length > termo.length && palavra.includes(termo)) return termo;
    }
  }
  return null;
}

/**
 * O exame de um texto qualquer contra as listas.
 *
 * Fica exportado porque o nome, a sigla e, mais tarde, qualquer texto que a
 * pessoa escreva e outra pessoa leia passam pelo mesmo crivo, e duplicar a
 * regra é como as duas metades acabam discordando.
 */
export function examinarTexto(texto: string): ExameDeTexto {
  const palavras = normalizar(texto).split(" ").filter((palavra) => palavra.length > 0);
  const colado = palavras.join("");

  const barrado = acharExato(palavras, colado, BLOQUEADOS);
  if (barrado) return { veredicto: "bloqueado", termo: barrado };

  const parecido = acharParecido(palavras, colado);
  if (parecido) return { veredicto: "revisar", termo: parecido };

  return { veredicto: "livre", termo: null };
}

// ---------------------------------------------------------------------------
// A sigla
// ---------------------------------------------------------------------------

/**
 * A sigla tem três letras de A a Z, sem número e sem acento.
 *
 * Número ficou de fora por dois motivos que se somam. O filtro troca 4 por a, 3
 * por e, 1 por i, 0 por o e 5 por s antes de comparar, então aceitar número
 * seria abrir o caminho mais curto para escrever o que a lista barra. E a sigla
 * é desenhada com corpo 13 dentro de um escudo de 40 pixels: nesse tamanho 0 e
 * O são a mesma mancha, e dois clubes diferentes ficariam iguais na tabela, que
 * é justamente onde a sigla existe para distinguir.
 *
 * Acento ficou de fora porque a sigla é comparada com as do mundo inteiro, e
 * SÃO digitado de três jeitos viraria três clubes diferentes.
 */
export const SIGLA_LETRAS = 3;

const SIGLA_CERTA = /^[A-Z]{3}$/;

/** Quantas siglas livres a tela oferece quando a escolhida não cabe. */
export const SUGESTOES_MINIMAS = 3;

/**
 * Quantos caracteres o nome precisa ter para o clube nascer.
 *
 * Contado no texto cru, e não nas letras normalizadas, porque um nome em
 * alfabeto que não é o latino sobrevive ao campo e some da normalização. O
 * filtro é escrito em alfabeto latino e simplesmente não tem o que dizer sobre
 * ele, o que é diferente de recusá-lo.
 */
export const NOME_MINIMO = 2;

/**
 * A sigla como ela é gravada, para o campo chamar a cada tecla.
 *
 * Corta no tamanho certo em vez de recusar: quem digita o nome inteiro no campo
 * da sigla vê as três primeiras letras aparecerem, que é o que a pessoa espera.
 */
export function normalizarSigla(texto: string): string {
  return semAcento(texto)
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, SIGLA_LETRAS);
}

function conjuntoDeSiglas(existentes: Iterable<string>): Set<string> {
  const tomadas = new Set<string>();
  for (const sigla of existentes) tomadas.add(normalizarSigla(sigla));
  return tomadas;
}

// ---------------------------------------------------------------------------
// O que a tela recebe
// ---------------------------------------------------------------------------

/**
 * As chaves de i18n que saem daqui, escritas como texto solto de propósito.
 *
 * Amarrá-las ao tipo `Chave` obrigaria a importar o i18n, e esta pasta não pode:
 * `src/game/` roda no worker, que carrega a própria cópia do i18n e nunca recebe
 * o idioma escolhido. `idioma.test.ts` cobra isso arquivo por arquivo, e o único
 * jeito de passar seria abrir mais uma exceção numa regra que já custou caro.
 *
 * O preço é que um rename no dicionário não quebra nada aqui. Quem cobra a
 * ponte é `clube-criado.test.ts`, que pode importar os dois lados porque a
 * varredura pula os arquivos de teste: lá as seis chaves são abertas no
 * dicionário dos três idiomas, e a tabela que as lista é tipada contra `Chave`.
 */
export type ChaveDoNome = "criador.nome.bloqueado" | "criador.nome.duvidoso";

export type ChaveDaSigla =
  | "criador.sigla.formato"
  | "criador.sigla.jaExiste"
  | "criador.sigla.bloqueado"
  | "criador.sigla.duvidoso";

/**
 * O veredicto de um campo, do jeito que a tela consome.
 *
 * `aceito` e `revisar` são independentes de propósito: o campo marcado é aceito
 * e cria o clube, só que o mundo guarda que ele passou perto. A chave vem
 * tipada para a tela não escrever uma que não existe no dicionário.
 */
export interface AvisoDoCriador<Chave extends string> {
  aceito: boolean;
  revisar: boolean;
  chave: Chave | null;
  termo: string | null;
}

/** O nome do clube contra as listas, sem nenhuma outra regra por cima. */
export function conferirNome(nome: string): AvisoDoCriador<ChaveDoNome> {
  const exame = examinarTexto(nome);
  if (exame.veredicto === "bloqueado") {
    return { aceito: false, revisar: false, chave: "criador.nome.bloqueado", termo: exame.termo };
  }
  if (exame.veredicto === "revisar") {
    return { aceito: true, revisar: true, chave: "criador.nome.duvidoso", termo: exame.termo };
  }
  return { aceito: true, revisar: false, chave: null, termo: null };
}

/**
 * A sigla contra o formato, contra as listas e contra o mundo, nessa ordem.
 *
 * A ordem não é arbitrária. Formato primeiro porque é sobre o que a pessoa
 * acabou de digitar. Termo barrado antes de sigla repetida porque a repetida
 * vem acompanhada das livres ao lado, e oferecer alternativa para um xingamento
 * é convidar a tentar a próxima. Marca por último porque ela não impede nada e
 * não deve roubar o lugar de um recado que impede.
 */
export function conferirSigla(
  sigla: string,
  existentes: Iterable<string>,
): AvisoDoCriador<ChaveDaSigla> {
  const bruta = semAcento(sigla).trim().toUpperCase();
  if (!SIGLA_CERTA.test(bruta)) {
    return { aceito: false, revisar: false, chave: "criador.sigla.formato", termo: null };
  }

  const exame = examinarTexto(bruta);
  if (exame.veredicto === "bloqueado") {
    return { aceito: false, revisar: false, chave: "criador.sigla.bloqueado", termo: exame.termo };
  }

  if (conjuntoDeSiglas(existentes).has(bruta)) {
    return { aceito: false, revisar: false, chave: "criador.sigla.jaExiste", termo: null };
  }

  if (exame.veredicto === "revisar") {
    return { aceito: true, revisar: true, chave: "criador.sigla.duvidoso", termo: exame.termo };
  }

  return { aceito: true, revisar: false, chave: null, termo: null };
}

// ---------------------------------------------------------------------------
// As siglas sugeridas
// ---------------------------------------------------------------------------

/**
 * Palavras que não viram sigla.
 *
 * Ligação não carrega significado, e sigla de clube quase nunca guarda a
 * genérica: Sociedade Esportiva Palmeiras é PAL para todo mundo, nunca SEP. Mas
 * a filtragem só vale se sobrar alguma coisa, senão Futebol Clube viraria nada.
 */
const LIGACOES: ReadonlySet<string> = new Set([
  "de",
  "da",
  "do",
  "das",
  "dos",
  "e",
  "y",
  "del",
  "la",
  "el",
  "los",
  "las",
  "of",
  "the",
  "fc",
  "ec",
  "sc",
  "ac",
  "cf",
  "cd",
  "cr",
  "sad",
]);

const GENERICOS: ReadonlySet<string> = new Set([
  "clube",
  "club",
  "futebol",
  "futbol",
  "football",
  "associacao",
  "asociacion",
  "association",
  "sociedade",
  "sociedad",
  "esporte",
  "esportivo",
  "esportiva",
  "deportivo",
  "deportiva",
  "united",
  "team",
]);

const ALFABETO = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** As palavras que dão sentido ao nome, em maiúsculas. */
function palavrasSignificativas(nome: string): string[] {
  const todas = normalizar(nome)
    .split(" ")
    .filter((palavra) => palavra.length > 0);
  const uteis = todas.filter((palavra) => !LIGACOES.has(palavra) && !GENERICOS.has(palavra));
  return (uteis.length > 0 ? uteis : todas).map((palavra) => palavra.toUpperCase());
}

/**
 * A palavra sem as vogais do meio, que é como a sigla soa quando o nome tem uma
 * palavra só: Santos vira SNT e Palmeiras vira PLM.
 */
function esqueleto(palavra: string): string {
  const consoantes = [...palavra.slice(1)].filter((letra) => !"AEIOU".includes(letra));
  return (palavra.slice(0, 1) + consoantes.join("")).slice(0, SIGLA_LETRAS);
}

/**
 * As siglas que o nome sugere, da mais parecida com o nome para a menos.
 *
 * A ordem é o produto. De Atlético Vitória saem AVI, ATV e VIT antes de
 * qualquer outra coisa, porque são as três que alguém escreveria à mão, e a
 * primeira livre é a que a tela oferece primeiro.
 */
function candidatasDoNome(palavras: string[]): string[] {
  const candidatas: string[] = [];
  const primeira = palavras[0] ?? "";
  const ultima = palavras[palavras.length - 1] ?? "";

  if (palavras.length >= 3) {
    candidatas.push(
      palavras
        .slice(0, 3)
        .map((palavra) => palavra.slice(0, 1))
        .join(""),
    );
  }

  if (palavras.length >= 2) {
    candidatas.push(primeira.slice(0, 1) + ultima.slice(0, 2));
    candidatas.push(primeira.slice(0, 2) + ultima.slice(0, 1));
    candidatas.push(ultima.slice(0, 3));
    candidatas.push(primeira.slice(0, 3));
    candidatas.push(primeira.slice(0, 1) + esqueleto(ultima).slice(0, 2));
    candidatas.push(esqueleto(ultima));
    candidatas.push(esqueleto(primeira));
  } else {
    candidatas.push(primeira.slice(0, 3));
    candidatas.push(esqueleto(primeira));
    candidatas.push(primeira.slice(0, 1) + primeira.slice(-2));
    candidatas.push(primeira.slice(0, 2) + primeira.slice(-1));
  }

  return candidatas;
}

/**
 * O fim da fila, quando o nome não dá mais de si.
 *
 * Num mundo de trezentos clubes as três siglas óbvias de um nome curto podem
 * estar todas tomadas, e a tela não pode ficar sem nada a oferecer. Varia a
 * última letra e depois a do meio, mantendo as duas primeiras do nome, que é o
 * que ainda lembra o clube.
 */
function ladeira(letras: string): string[] {
  const semente = (letras + letras + "XX").slice(0, 2);
  const extras: string[] = [];
  for (const letra of ALFABETO) extras.push(semente + letra);
  for (const letra of ALFABETO) extras.push(semente.slice(0, 1) + letra + semente.slice(1, 2));
  return extras;
}

/**
 * Siglas livres tiradas do nome do clube.
 *
 * Elas nunca colidem com o mundo e nunca caem nas listas: sugerir uma sigla que
 * o próprio filtro barraria seria o jogo propondo o xingamento.
 *
 * Nome sem letra latina devolve lista vazia, e a pessoa escreve a sigla à mão.
 * Inventar três letras do nada ali seria oferecer a sigla de outro clube.
 */
export function sugerirSiglas(
  nome: string,
  existentes: Iterable<string>,
  quantas: number = SUGESTOES_MINIMAS,
): string[] {
  const letras = normalizar(nome).replace(/\s+/g, "").toUpperCase();
  if (letras.length === 0) return [];

  const tomadas = conjuntoDeSiglas(existentes);
  const vistas = new Set<string>();
  const livres: string[] = [];

  for (const bruta of [...candidatasDoNome(palavrasSignificativas(nome)), ...ladeira(letras)]) {
    if (livres.length >= quantas) break;
    const candidata = bruta.toUpperCase();
    if (!SIGLA_CERTA.test(candidata) || vistas.has(candidata)) continue;
    vistas.add(candidata);
    if (tomadas.has(candidata)) continue;
    if (examinarTexto(candidata).veredicto !== "livre") continue;
    livres.push(candidata);
  }

  return livres;
}

// ---------------------------------------------------------------------------
// Os dois campos juntos
// ---------------------------------------------------------------------------

export interface ExameDoClubeCriado {
  nome: AvisoDoCriador<ChaveDoNome>;
  sigla: AvisoDoCriador<ChaveDaSigla>;
  /** Se o botão de confirmar pode agir. */
  podeCriar: boolean;
  /** Se o clube nasce marcado para quem revisa. */
  revisar: boolean;
}

/**
 * Os dois campos de uma vez, que é como a tela decide se o botão age.
 *
 * O nome curto demais derruba `podeCriar` sem chave nenhuma, e é de propósito:
 * campo ainda não preenchido não é erro, e escrever um recado ali seria explicar
 * ao jogador o que ele está vendo.
 */
export function avaliarClubeCriado(
  nome: string,
  sigla: string,
  siglasDoMundo: Iterable<string>,
): ExameDoClubeCriado {
  const exameDoNome = conferirNome(nome);
  const exameDaSigla = conferirSigla(sigla, siglasDoMundo);
  const nomeSuficiente = nome.trim().length >= NOME_MINIMO;

  return {
    nome: exameDoNome,
    sigla: exameDaSigla,
    podeCriar: nomeSuficiente && exameDoNome.aceito && exameDaSigla.aceito,
    revisar: exameDoNome.revisar || exameDaSigla.revisar,
  };
}
